import { ref, nextTick, Ref, ComputedRef } from 'vue';
import { cloneDeep } from 'lodash-es';
import {
  MAXIMUM_HISTORY_SIZE,
  TOKEN_STATUS,
} from '@/common/components/molecules/filterSearch/filterSearch.define';
import {
  EditingFilterToken,
  FilterDropdownInfo,
  FilterDropdownItem,
  FilterHistory,
  FilterItem,
  FilterToken,
} from '@/common/components/molecules/filterSearch/filterSearch.type';

interface UseFilterKeyboardParams {
  filterItems: Ref<FilterItem[]>;
  tokenList: Ref<FilterToken[]>;
  editingToken: Ref<EditingFilterToken | null>;
  lastToken: ComputedRef<FilterToken>;
  isFilterActive: Ref<boolean>;
  dropdownListInfo: FilterDropdownInfo;
  isShowDropdown: Ref<boolean>;
  hoveredDropdownItemIndex: Ref<number>;
  isKeyboardNavigating: Ref<boolean>;
  filterHistory: Ref<FilterHistory[]>;
  isShowFilterHistoryList: ComputedRef<boolean>;
  mainInputRef: Ref<any>;
  mainInputValue: Ref<string>;
  isFocus: Ref<boolean>;
  isExpand: Ref<boolean>;
  tokenRefs: Ref<HTMLElement[]>;
  activateFilter: () => Promise<void>;
  initHoveredDropdownItemIndex: () => void;
  showDropdownList: () => void;
  hideDropdownList: () => void;
  filterByInput: (searchWord: string) => void;
  setKeyDropdownList: () => void;
  setOperatorDropdownList: (keyId: string) => void;
  setDropdownListByLastToken: () => void;
  scrollSelectedItemIntoView: () => void;
  focusMainInput: () => Promise<void>;
  focusPartsInput: (targetTokenID: number) => void;
  setMoreCount: () => Promise<void>;
  setTokenParts: (selectedDropdownItem: FilterDropdownItem | null) => Promise<void>;
  createDraftToken: () => Promise<void>;
  quitEditingToken: () => Promise<void>;
  onClickResumeToken: (targetToken: FilterToken) => Promise<void>;
  saveHistory: (targetToken: FilterToken) => Promise<void>;
  getEmitComplete: () => () => void;
}

export function useFilterKeyboard({
  filterItems,
  tokenList,
  editingToken,
  lastToken,
  isFilterActive,
  dropdownListInfo,
  isShowDropdown,
  hoveredDropdownItemIndex,
  isKeyboardNavigating,
  filterHistory,
  isShowFilterHistoryList,
  mainInputRef,
  mainInputValue,
  isFocus,
  isExpand,
  tokenRefs,
  activateFilter,
  initHoveredDropdownItemIndex,
  showDropdownList,
  hideDropdownList,
  filterByInput,
  setKeyDropdownList,
  setOperatorDropdownList,
  setDropdownListByLastToken,
  scrollSelectedItemIntoView,
  focusMainInput,
  focusPartsInput,
  setMoreCount,
  setTokenParts,
  createDraftToken,
  quitEditingToken,
  onClickResumeToken,
  saveHistory,
  getEmitComplete,
}: UseFilterKeyboardParams) {
  const isComposing = ref<boolean>(false);
  const isHandlingFocus = ref<boolean>(false);
  const skipNextDraftCreation = ref<boolean>(false);

  const onCompositionStart = () => {
    isComposing.value = true;
  };

  const onCompositionEnd = () => {
    isComposing.value = false;
  };

  const onInput = (e: Event) => {
    const typedEvent = e as InputEvent;
    const target = typedEvent.target as HTMLInputElement;
    if (target) {
      const inputText = target?.value ?? typedEvent;

      filterByInput(inputText);
      showDropdownList();
      initHoveredDropdownItemIndex();
    }
  };

  const onKeyUpDown = (e: KeyboardEvent, direction: 'up' | 'down') => {
    e.preventDefault();

    if (!isShowDropdown.value) {
      return;
    }

    isKeyboardNavigating.value = true;

    const currIndex: number = hoveredDropdownItemIndex.value;

    if (direction === 'up') {
      hoveredDropdownItemIndex.value = Math.max(currIndex - 1, -1);
    } else if (direction === 'down') {
      const maxIndex = isShowFilterHistoryList.value
        ? dropdownListInfo.filteredItems.length +
          Math.min(filterHistory.value.length, MAXIMUM_HISTORY_SIZE) -
          1
        : dropdownListInfo.filteredItems.length - 1;
      hoveredDropdownItemIndex.value = Math.min(currIndex + 1, maxIndex);
    }

    if (hoveredDropdownItemIndex.value > -1) {
      scrollSelectedItemIntoView();
    }
  };

  const addTokenByHistory = async (selectedHistory: FilterHistory) => {
    if (editingToken.value?.status === TOKEN_STATUS.KEY_ING && !editingToken.value?.key?.id) {
      const draftIndex = tokenList.value.findIndex((t) => t.id === editingToken.value?.id);
      if (draftIndex > -1) {
        tokenList.value.splice(draftIndex, 1);
        tokenRefs.value.splice(draftIndex, 1);
      }
      editingToken.value = null;
      dropdownListInfo.isEditing = false;
    }

    const { key, operator, values } = cloneDeep(selectedHistory.token);

    tokenList.value.push({
      id: Date.now() + Math.random(),
      key,
      operator,
      values,
      status: TOKEN_STATUS.VALUE_DONE,
    });

    await setMoreCount();
    await createDraftToken();
  };

  const createDefaultSearchTokens = async (inputText: string, target: HTMLInputElement) => {
    if (editingToken.value?.status === TOKEN_STATUS.KEY_ING && !editingToken.value?.key?.id) {
      const draftIndex = tokenList.value.findIndex((t) => t.id === editingToken.value?.id);
      if (draftIndex > -1) {
        tokenList.value.splice(draftIndex, 1);
        tokenRefs.value.splice(draftIndex, 1);
      }
      editingToken.value = null;
      dropdownListInfo.isEditing = false;
    }

    const newTokens: FilterToken[] = [];
    filterItems.value.forEach((item) => {
      if (item.defaultSearchField) {
        const newToken: FilterToken = {
          id: Date.now() + Math.random(),
          key: item.key,
          operator: { id: 'like', name: 'like' },
          values: [{ id: inputText, name: inputText }],
          status: TOKEN_STATUS.VALUE_DONE,
        };
        newTokens.push(newToken);
        tokenList.value.push(newToken);
      }
    });

    await Promise.all(newTokens.map((token) => saveHistory(token)));

    mainInputValue.value = '';
    target.value = '';

    getEmitComplete()();
    hideDropdownList();
    await setMoreCount();
    await createDraftToken();
  };

  const onKeyEnter = async (e: KeyboardEvent) => {
    if (isComposing.value) {
      return;
    }

    const target = e.target as HTMLInputElement;
    if (!target) {
      return;
    }
    const inputText = target?.value?.trim();

    const hasInputText = !!inputText;
    const hasDefaultSearchField = filterItems.value.some((item) => item.defaultSearchField);
    const isDraftKeyIngToken =
      editingToken.value?.status === TOKEN_STATUS.KEY_ING && !editingToken.value?.key?.id;
    const isNoTokenInProgress =
      !tokenList.value.length ||
      (lastToken.value && lastToken.value.status === TOKEN_STATUS.VALUE_DONE);
    const isNotEditingToken = !editingToken.value;
    const isMatchingFilterKey = filterItems.value.some((item) => {
      const normalizedInputText = inputText.toLowerCase().replace(/\s/g, '');
      const normalizedKeyId = item.key.id.toLowerCase().replace(/\s/g, '');
      const normalizedKeyName = item.key.name.toLowerCase().replace(/\s/g, '');
      return normalizedKeyId === normalizedInputText || normalizedKeyName === normalizedInputText;
    });
    const isNotSelectingDropdownItem = hoveredDropdownItemIndex.value === -1;

    if (
      hasInputText &&
      hasDefaultSearchField &&
      (isNoTokenInProgress || isDraftKeyIngToken) &&
      (isNotEditingToken || isDraftKeyIngToken) &&
      !isMatchingFilterKey &&
      isNotSelectingDropdownItem
    ) {
      await createDefaultSearchTokens(inputText, target);
      return;
    }

    if (!isShowDropdown.value) {
      return;
    }

    if (isShowFilterHistoryList.value) {
      const historyIndex = hoveredDropdownItemIndex.value - dropdownListInfo.filteredItems.length;
      const reversedHistoryIndex = filterHistory.value.length - 1 - historyIndex;
      const hoveredHistoryItem = filterHistory.value?.[reversedHistoryIndex];
      if (hoveredHistoryItem) {
        await addTokenByHistory(hoveredHistoryItem);
        return;
      }
    }

    const hoveredDropdownItem = dropdownListInfo.filteredItems?.[hoveredDropdownItemIndex.value];

    const matchedDropdownItem = dropdownListInfo.filteredItems.find((item) => {
      const normalizedItemName = item.name.toLowerCase().replace(/\s/g, '');
      const normalizedInputText = inputText.toLowerCase().replace(/\s/g, '');
      return normalizedItemName === normalizedInputText;
    });

    const userInputInfo =
      (!dropdownListInfo.items?.length || dropdownListInfo.isAutoValueItems) && inputText
        ? { id: inputText, name: inputText }
        : null;
    const prevEditingStatus = editingToken.value?.status;
    const wasCreationMode =
      prevEditingStatus &&
      prevEditingStatus !== TOKEN_STATUS.VALUE_DONE &&
      prevEditingStatus !== TOKEN_STATUS.EMPTY;

    await setTokenParts(hoveredDropdownItem ?? matchedDropdownItem ?? userInputInfo);

    if (wasCreationMode && !editingToken.value) {
      await createDraftToken();
      return;
    }

    const finalInputText = editingToken.value ? inputText : mainInputValue.value;
    if (dropdownListInfo.multi && finalInputText) {
      filterByInput(finalInputText);
    }
  };

  const onKeyCtrlEnter = async () => {
    if (!dropdownListInfo.multi || !dropdownListInfo.items?.length) {
      return;
    }

    if (!editingToken.value) {
      if (lastToken.value.values?.length) {
        lastToken.value.status = TOKEN_STATUS.VALUE_DONE;
        setDropdownListByLastToken();
        filterByInput(mainInputValue.value);
      } else {
        hideDropdownList();
      }
    } else {
      await quitEditingToken();
      await focusMainInput();
    }
  };

  const onKeyBackspace = () => {
    if (isComposing.value) {
      return;
    }

    if (!mainInputValue.value) {
      const isDraftToken =
        lastToken.value?.status === TOKEN_STATUS.KEY_ING && !lastToken.value?.key?.id;
      if (isDraftToken) {
        return;
      }

      const lastCompletedIndex = [...tokenList.value]
        .reverse()
        .findIndex(({ status }) => status === TOKEN_STATUS.VALUE_DONE);

      if (lastCompletedIndex > -1) {
        const actualIndex = tokenList.value.length - 1 - lastCompletedIndex;
        tokenList.value.splice(actualIndex, 1);
        tokenRefs.value.splice(actualIndex, 1);
      }

      if (dropdownListInfo.category !== 'key') {
        setKeyDropdownList();
      }

      getEmitComplete()();
    }
  };

  const onKeyEsc = async () => {
    const isDraftToken =
      editingToken.value?.status === TOKEN_STATUS.KEY_ING && !editingToken.value?.key?.id;

    if (editingToken.value) {
      if (dropdownListInfo.multi) {
        const targetToken = tokenList.value.find((token) => token.id === editingToken.value?.id);
        if (targetToken) {
          targetToken.values = cloneDeep(editingToken.value.backupValues);
        }
      }

      await quitEditingToken();
    }

    isExpand.value = false;
    hideDropdownList();

    if (isDraftToken) {
      isFocus.value = false;
      mainInputRef.value?.blur();
    }

    if (dropdownListInfo.multi && !editingToken.value) {
      lastToken.value.status = TOKEN_STATUS.VALUE_DONE;
    }
  };

  const onTokenBackspace = async (tokenIndex: number) => {
    if (isComposing.value) {
      return;
    }

    const targetToken = tokenList.value[tokenIndex];
    if (!targetToken || !editingToken.value || editingToken.value.id !== targetToken.id) {
      return;
    }

    const status = targetToken.status;

    if (status === TOKEN_STATUS.KEY_ING && !targetToken.key?.id) {
      const lastCompletedIndex = tokenList.value.findLastIndex(
        (t) => t.status === TOKEN_STATUS.VALUE_DONE && t.id !== targetToken.id,
      );

      if (lastCompletedIndex > -1) {
        tokenList.value.splice(lastCompletedIndex, 1);
        tokenRefs.value.splice(lastCompletedIndex, 1);
        setKeyDropdownList();
        await nextTick();
        focusPartsInput(targetToken.id);
        getEmitComplete()();
        await setMoreCount();
      }
    } else if (status === TOKEN_STATUS.OPERATOR_ING) {
      targetToken.key = { id: '', name: '' };
      targetToken.status = TOKEN_STATUS.KEY_ING;
      editingToken.value.key = { id: '', name: '' };
      editingToken.value.status = TOKEN_STATUS.KEY_ING;
      setKeyDropdownList();
      await nextTick();
      focusPartsInput(targetToken.id);
    } else if (status === TOKEN_STATUS.OPERATOR_DONE || status === TOKEN_STATUS.VALUE_ING) {
      targetToken.operator = undefined;
      targetToken.values = [];
      targetToken.status = TOKEN_STATUS.OPERATOR_ING;
      editingToken.value.operator = undefined;
      editingToken.value.values = [];
      editingToken.value.status = TOKEN_STATUS.OPERATOR_ING;
      setOperatorDropdownList(targetToken.key.id);
      await nextTick();
      focusPartsInput(targetToken.id);
    }
  };

  const onFocusMainInput = async () => {
    if (isHandlingFocus.value) {
      return;
    }
    isHandlingFocus.value = true;

    try {
      if (!isFilterActive.value) {
        await activateFilter();
      }

      if (skipNextDraftCreation.value) {
        skipNextDraftCreation.value = false;
        isFocus.value = true;
        isExpand.value = true;
        setDropdownListByLastToken();
        showDropdownList();
        return;
      }

      if (editingToken.value && editingToken.value.id === lastToken.value?.id) {
        await nextTick();
        focusPartsInput(editingToken.value.id);
        return;
      }

      const lastTokenStatus = lastToken.value?.status;
      if (
        lastTokenStatus &&
        lastTokenStatus !== TOKEN_STATUS.VALUE_DONE &&
        lastTokenStatus !== TOKEN_STATUS.EMPTY
      ) {
        await onClickResumeToken(lastToken.value);
        return;
      }

      await quitEditingToken();
      await createDraftToken();
    } finally {
      isHandlingFocus.value = false;
    }
  };

  const deactivateFilterSearch = async () => {
    isExpand.value = false;
    hideDropdownList();

    if (editingToken.value) {
      await quitEditingToken();
    } else if (dropdownListInfo.multi && !editingToken.value && lastToken.value.values?.length) {
      lastToken.value.status = TOKEN_STATUS.VALUE_DONE;
    }

    isFocus.value = false;
    await setMoreCount();
  };

  const onClickOutside = deactivateFilterSearch;
  const onWheelOutside = deactivateFilterSearch;

  return {
    isComposing,
    onCompositionStart,
    onCompositionEnd,
    onInput,
    onKeyUpDown,
    addTokenByHistory,
    createDefaultSearchTokens,
    onKeyEnter,
    onKeyCtrlEnter,
    onKeyBackspace,
    onKeyEsc,
    onTokenBackspace,
    onFocusMainInput,
    onClickOutside,
    onWheelOutside,
  };
}
